// Lib
import React, { ReactElement } from 'react';
import PropTypes from 'prop-types';
import { pure, compose } from '../../../node_module_clones/recompose';
import classNames from 'classnames';
import { createSelector } from 'reselect';
import { connect } from 'react-redux';

// Utils
import { getUserId } from '../../../common/users/utils/userPropertyUtils';
import { getGridSize } from '../../utils/grid/gridSizeSelector';
import { scaleToGrid } from '../../utils/grid/gridUtils';
import { getDisplayName } from '../../user/userUtil';
import { isUserActiveOnCurrentBoard } from './userAvatarUtils';

// Components
import UserAvatar from './UserAvatar';
import TooltipSource from '../tooltips/TooltipSource';
import UserAvatarCount from './UserAvatarCount';

// Types
import { MNUser } from '../../../common/users/userModelTypes';
import { UserAvatarInfo } from './userAvatarTypes';

// Styles
import './UserAvatarGroup.scss';

type UserAvatarListProps = {
    users: Array<MNUser | UserAvatarInfo>;
    currentBoardId: string;
    limit: number;
    size: number;
    gridSize: number;
    getTooltipOffset: () => number;
    tooltipEnabled: boolean;
    tooltipDelay: number;
    hiddenUserCount: number;
};

/**
 * Separated into its own component so that it can be shared with the HeaderShareAvatars.
 */
export const UserAvatarList = ({
    users,
    currentBoardId,
    limit = 2,
    size,
    gridSize,
    getTooltipOffset,
    tooltipEnabled = true,
    tooltipDelay = 700,
    hiddenUserCount,
}: UserAvatarListProps): ReactElement => {
    const showCount = hiddenUserCount >= 1;
    return (
        <>
            {showCount && (
                <div className="TooltipSource">
                    <div className="avatar-container">
                        <UserAvatarCount hiddenUserCount={hiddenUserCount} gridSize={gridSize} size={size} />
                    </div>
                </div>
            )}
            {users
                .slice(0, limit)
                .reverse()
                .map((user, index: number) => {
                    // In some cases we pass in an array of UserAvatarInfo with isActiveOnCurrentBoard rather than
                    // currentBoardId to avoid unnecessary re-renders, so check for this first, otherwise do the regular check
                    const isActive =
                        user && 'isActiveOnCurrentBoard' in user
                            ? user.isActiveOnCurrentBoard
                            : isUserActiveOnCurrentBoard(user, currentBoardId);

                    return (
                        <TooltipSource
                            key={getUserId(user) || index}
                            enabled={tooltipEnabled}
                            tooltipText={getDisplayName(user)}
                            pollPosition
                            distance={10}
                            offset={getTooltipOffset ? getTooltipOffset() : 0}
                            delay={tooltipDelay}
                            triggerOnMouseEnter
                        >
                            <div className="avatar-container">
                                <UserAvatar user={user} size={size} active={isActive} />
                            </div>
                        </TooltipSource>
                    );
                })}
        </>
    );
};

const mapStateToProps = createSelector(
    getGridSize,
    (_: never, ownProps: UserAvatarListProps) => ownProps.size,
    (gridSize, size) => ({
        fontSize: Math.round(Math.ceil(scaleToGrid(size, gridSize)) * 0.4),
        gridSize,
    }),
);

type UserAvatarGroupProps = UserAvatarListProps & {
    fontSize: number;
    className: string;
};

const UserAvatarGroup = (props: UserAvatarGroupProps): ReactElement | null => {
    const { className, users, fontSize } = props;

    if (!users || !users.length) return null;

    return (
        <div className={classNames('UserAvatarGroup', className)} style={{ fontSize }}>
            <UserAvatarList {...props} />
        </div>
    );
};

UserAvatarList.propTypes = UserAvatarGroup.propTypes = {
    users: PropTypes.array,
    size: PropTypes.number.isRequired,
    className: PropTypes.string,
    currentBoardId: PropTypes.string,
    fontSize: PropTypes.number,
    limit: PropTypes.number,
    getTooltipOffset: PropTypes.func,
    tooltipDelay: PropTypes.number,
};

const enhancer = compose(pure, connect(mapStateToProps));

// @ts-ignore not sure how to make compose function typesafe
export default enhancer(UserAvatarGroup);
